// Общая часть «сайт как приложение»: регистрация service worker, определение
// запуска из установленного приложения и перехват предложения установки.
//
// Живёт вне Stimulus, потому что событие `beforeinstallprompt` браузер шлёт
// один раз и рано — раньше, чем контроллер карточки успеет подключиться. Здесь
// оно перехватывается при загрузке скрипта и лежит до тех пор, пока карточка
// не спросит.

const SERVICE_WORKER_PATH = "/service-worker"

// Единственное предложение установки, которое дал браузер, — или null, если он
// его ещё не давал (или уже установлено, или это Safari, который его не даёт
// вовсе).
let deferredInstallPrompt = null
let registration = null

export function serviceWorkerSupported() {
  // Незащищённый контекст — это `bin/dev` по http на телефоне: там нет ни
  // service worker, ни уведомлений, и это не ошибка.
  return "serviceWorker" in navigator && window.isSecureContext
}

export function pushSupported() {
  return serviceWorkerSupported() && "PushManager" in window && "Notification" in window
}

// Запущено ли из установленного приложения. `navigator.standalone` — iOS: там
// display-mode приходит не всегда.
export function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true
}

// iOS не показывает предложение установки: там это делается вручную через
// «Поделиться» → «На экран «Домой»», и рассказать об этом может только сайт.
export function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) ||
    // iPadOS притворяется макбуком; отличает его наличие тач-экрана.
    (window.navigator.platform === "MacIntel" && window.navigator.maxTouchPoints > 1)
}

export function installPromptAvailable() {
  return deferredInstallPrompt !== null
}

// Показывает системное окно установки. Возвращает true, если человек согласился.
export async function promptInstall() {
  if (!deferredInstallPrompt) return false

  const prompt = deferredInstallPrompt
  // Событие одноразовое: второй `prompt()` по нему бросит исключение, поэтому
  // ссылка снимается до показа, а не после.
  deferredInstallPrompt = null
  prompt.prompt()

  const { outcome } = await prompt.userChoice
  return outcome === "accepted"
}

export function registerServiceWorker() {
  if (!serviceWorkerSupported()) return Promise.resolve(null)

  registration ||= navigator.serviceWorker
    .register(SERVICE_WORKER_PATH, { scope: "/" })
    .catch((error) => {
      console.warn("Service worker registration failed", error)
      // Сбрасываем, чтобы следующий заход попробовал заново, а не получил
      // навсегда запомненный отказ.
      registration = null
      return null
    })

  return registration
}

// Регистрация резолвится раньше, чем воркер станет активным, а
// `pushManager.subscribe()` на неактивном воркере бросает AbortError — первый
// в жизни заход на страницу и сразу нажатая кнопка попадали бы именно в это.
// `navigator.serviceWorker.ready` резолвится только активным; ждать его можно
// лишь после register(), иначе он не резолвится вовсе.
export async function activeServiceWorker() {
  const registered = await registerServiceWorker()
  if (!registered) return null

  return navigator.serviceWorker.ready
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    // Без этого браузер покажет собственную плашку установки, и кнопка в
    // кабинете будет второй такой же.
    event.preventDefault()
    deferredInstallPrompt = event
    window.dispatchEvent(new CustomEvent("pwa:installable"))
  })

  window.addEventListener("appinstalled", () => {
    deferredInstallPrompt = null
    window.dispatchEvent(new CustomEvent("pwa:installed"))
  })
}
